import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    FaMicrochip,
    FaBarcode,
    FaCalendarAlt,
    FaHourglassEnd,
    FaTimes,
    FaRedo
} from 'react-icons/fa';

import { request } from '../../services/api';
import styles from './UserMenu.module.css';

const normalizeDevice = (device = {}) => {
    return {
        id: device.id ?? '',
        serial: device.device_serial ?? '',
        name: device.device_name ?? '',
        startDate: device.start_date ?? '',
        dueDate: device.due_date ?? '',
        createdAt: device.created_at ?? ''
    };
};

const formatDate = (dateValue) => {
    if (!dateValue) return '---';

    return String(dateValue);
};

const UserDevicesModal = ({
    isOpen,
    onClose
}) => {
    const [devices, setDevices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const loadDevices = async () => {
        try {
            setIsLoading(true);
            setErrorMessage('');

            const response = await request('/user_get_devices.php', 'GET');

            if (response?.status !== 'success') {
                throw new Error(response?.message || 'خطا در دریافت اطلاعات دستگاه‌ها');
            }

            const nextDevices = Array.isArray(response.devices)
                ? response.devices.map(normalizeDevice)
                : [];

            setDevices(nextDevices);
        } catch (error) {
            console.error('خطا در دریافت دستگاه‌های کاربر:', error);
            setErrorMessage(error.message || 'خطا در دریافت اطلاعات دستگاه‌ها');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        loadDevices();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className={styles.userInfoModalOverlay}
            onMouseDown={onClose}
        >
            <div
                className={`${styles.userInfoModalBox} ${styles.userDevicesModalBox}`}
                onMouseDown={(e) => e.stopPropagation()}
                dir="rtl"
            >
                <div className={styles.userInfoModalHeader}>
                    <div className={styles.userInfoModalTitleBox}>
                        <FaMicrochip className={styles.userInfoModalIcon} />

                        <div>
                            <div className={styles.userInfoModalTitle}>
                                دستگاه‌های من
                            </div>

                            <div className={styles.userInfoModalSubTitle}>
                                مشاهده دستگاه‌های متصل به حساب کاربری
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className={styles.userInfoModalClose}
                        onClick={onClose}
                        title="بستن"
                    >
                        ×
                    </button>
                </div>

                <div className={styles.userInfoModalBody}>
                    {isLoading && (
                        <div className={styles.userDevicesStateBox}>
                            در حال دریافت اطلاعات دستگاه‌ها...
                        </div>
                    )}

                    {!isLoading && errorMessage && (
                        <div className={styles.userDevicesErrorBox}>
                            <span>{errorMessage}</span>

                            <button
                                type="button"
                                className={styles.userDevicesRetryBtn}
                                onClick={loadDevices}
                            >
                                <FaRedo />
                                تلاش مجدد
                            </button>
                        </div>
                    )}

                    {!isLoading && !errorMessage && devices.length === 0 && (
                        <div className={styles.userDevicesStateBox}>
                            دستگاهی برای این حساب کاربری ثبت نشده است.
                        </div>
                    )}

                    {!isLoading && !errorMessage && devices.length > 0 && (
                        <div className={styles.userDevicesGrid}>
                            {devices.map((device) => (
                                <div
                                    key={device.id || device.serial}
                                    className={styles.userDeviceCard}
                                >
                                    <div className={styles.userDeviceCardHeader}>
                                        <div className={styles.userDeviceIconBox}>
                                            <FaMicrochip />
                                        </div>

                                        <div className={styles.userDeviceTitleBox}>
                                            <div className={styles.userDeviceName}>
                                                {device.name || 'دستگاه بدون نام'}
                                            </div>

                                            <div className={styles.userDeviceSerialSmall}>
                                                {device.serial || '---'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.userDeviceInfoList}>
                                        <div className={styles.userDeviceInfoRow}>
                                            <span className={styles.userDeviceInfoLabel}>
                                                <FaBarcode />
                                                شماره سریال
                                            </span>

                                            <span className={styles.userDeviceInfoValue}>
                                                {device.serial || '---'}
                                            </span>
                                        </div>

                                        <div className={styles.userDeviceInfoRow}>
                                            <span className={styles.userDeviceInfoLabel}>
                                                <FaMicrochip />
                                                نام دستگاه
                                            </span>

                                            <span className={styles.userDeviceInfoValue}>
                                                {device.name || '---'}
                                            </span>
                                        </div>

                                        <div className={styles.userDeviceInfoRow}>
                                            <span className={styles.userDeviceInfoLabel}>
                                                <FaCalendarAlt />
                                                شروع قرارداد
                                            </span>

                                            <span className={styles.userDeviceInfoValue}>
                                                {formatDate(device.startDate)}
                                            </span>
                                        </div>

                                        <div className={styles.userDeviceInfoRow}>
                                            <span className={styles.userDeviceInfoLabel}>
                                                <FaHourglassEnd />
                                                پایان قرارداد
                                            </span>

                                            <span className={styles.userDeviceInfoValue}>
                                                {formatDate(device.dueDate)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.userInfoModalFooter}>
                   
                </div>
            </div>
        </div>,
        document.body
    );
};

export default UserDevicesModal;